import { RefreshCwIcon, WifiOffIcon } from "lucide-react";
import { SidebarMenuItem, SidebarMenuSkeleton } from "../ui/sidebar";

export type ProjectGroupedSidebarCloudStatusModel =
  | { readonly state: "loading" }
  | {
      readonly state: "stale";
      readonly label: string;
      readonly actionLabel: string;
      readonly onAction: () => void;
    }
  | {
      readonly state: "error";
      readonly label: string;
      readonly actionLabel: string;
      readonly onAction: () => void;
    };

export type ProjectGroupedSidebarEmptyStateModel =
  | ProjectGroupedSidebarCloudStatusModel
  | { readonly state: "empty"; readonly label: string };

export function ProjectGroupedSidebarEmptyState({
  model,
}: {
  readonly model: ProjectGroupedSidebarEmptyStateModel | null;
}) {
  if (model === null) return null;

  if (model.state === "loading") {
    return ["a", "b", "c", "d"].map((id) => (
      <SidebarMenuItem key={id} aria-hidden="true">
        <SidebarMenuSkeleton showIcon />
      </SidebarMenuItem>
    ));
  }

  if (model.state === "stale") {
    return (
      <SidebarMenuItem>
        <div
          aria-label="Projects showing cached data"
          className="flex min-h-8 items-center gap-1.5 pl-9 pr-2 text-[10px] text-warning-foreground"
        >
          <WifiOffIcon className="size-2.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{model.label}</span>
          <button
            type="button"
            className="shrink-0 text-muted-foreground/70 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
            onClick={model.onAction}
          >
            {model.actionLabel}
          </button>
        </div>
      </SidebarMenuItem>
    );
  }

  if (model.state === "error") {
    return (
      <SidebarMenuItem>
        <div
          aria-label="Projects loading error"
          className="flex min-h-10 flex-col items-start justify-center gap-0.5 pl-9 pr-2 text-[11px]"
        >
          <span className="text-destructive/80">{model.label}</span>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/70 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
            onClick={model.onAction}
          >
            <RefreshCwIcon className="size-2.5" />
            <span>{model.actionLabel}</span>
          </button>
        </div>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <div
        aria-label="Projects empty state"
        className="flex min-h-8 items-center pl-9 pr-2 text-[11px] text-muted-foreground/45"
      >
        {model.label}
      </div>
    </SidebarMenuItem>
  );
}
